'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/components/ui/use-toast'
import { Switch } from '@/components/ui/switch'
import { MultiSelect } from '@/components/ui/multi-select'
import { Loader2, Save, X, ExternalLink } from 'lucide-react'
import { Server, Sponsor } from '@/lib/db/schema'
import { COUNTRIES } from '@/lib/constants'

const SERVER_TAG_OPTIONS = [
  { 
    value: 'RR', 
    label: 'DNS RR', 
    description: 'DNS round-robin server (prioritized in server list)' 
  },
  { 
    value: 'Anycast', 
    label: 'Anycast', 
    description: 'Anycast routing server' 
  },
  { 
    value: 'IX', 
    label: 'IX', 
    description: 'Internet Exchange server' 
  },
  { 
    value: 'Hub', 
    label: 'Hub', 
    description: 'Hub server in the network topology' 
  },
  { 
    value: 'Leaf', 
    label: 'Leaf', 
    description: 'Leaf server in the network topology' 
  },
  { 
    value: 'Service', 
    label: 'Service', 
    description: 'Provides network services (NickServ, ChanServ, etc.)' 
  }
]

const serverSchema = z.object({
  hostname: z.string().min(1, 'Hostname is required').max(255),
  displayName: z.string().min(1, 'Display name is required').max(255),
  countryCode: z.string().length(2, 'Country code must be 2 characters'),
  city: z.string().max(100).optional(),
  region: z.enum(['Americas', 'Asia-Pacific', 'Europe', 'IMEA', 'Global'], { required_error: 'Region is required' }),
  ipv4: z.string().ip({ version: 'v4' }).optional().or(z.literal('')),
  ipv6: z.string().ip({ version: 'v6' }).optional().or(z.literal('')),
  ports: z.string().min(1, 'At least one port is required'),
  sslPorts: z.string().optional(),
  contactUrl: z.string().url().optional().or(z.literal('')),
  status: z.enum(['online', 'offline', 'unknown']).default('unknown'),
  sponsorId: z.string().optional(),
  isActive: z.boolean().default(true),
})

type ServerFormData = z.infer<typeof serverSchema>

interface ServerFormProps {
  server?: Server
  onCancel: () => void
  onSuccess: () => void
  ircSslEnabled?: boolean
}

export function ServerForm({ server, onCancel, onSuccess, ircSslEnabled = false }: ServerFormProps) {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [sponsors, setSponsors] = useState<Sponsor[]>([])
  const [sponsorsLoading, setSponsorsLoading] = useState(true)
  const [selectedTags, setSelectedTags] = useState<string[]>(server?.flags || [])
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ServerFormData>({
    resolver: zodResolver(serverSchema),
    defaultValues: server ? {
      hostname: server.hostname,
      displayName: server.displayName,
      countryCode: server.countryCode,
      city: server.city || '',
      region: server.region || '',
      ipv4: server.ipv4 || '',
      ipv6: server.ipv6 || '',
      ports: server.ports?.join(',') || '6667,6697',
      sslPorts: server.sslPorts?.join(',') || '6697',
      contactUrl: server.contactUrl || '',
      status: server.status || 'unknown',
      sponsorId: server.sponsorId || 'none',
      isActive: server.isActive ?? true,
    } : {
      ports: ircSslEnabled ? '6667,6697' : '6667',
      sslPorts: ircSslEnabled ? '6697' : '',
      status: 'unknown',
      sponsorId: 'none',
      isActive: true,
    }
  })

  // Fetch sponsors for the dropdown
  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await fetch('/api/sponsors')
        if (response.ok) {
          const data = await response.json()
          const activeSponsors = data.filter((sponsor: Sponsor) => sponsor.isActive)
          // Sort sponsors alphabetically by name
          activeSponsors.sort((a: Sponsor, b: Sponsor) => a.name.localeCompare(b.name))
          setSponsors(activeSponsors)
        }
      } catch (error) {
        console.error('Error fetching sponsors:', error)
      } finally {
        setSponsorsLoading(false)
      }
    }

    fetchSponsors()
  }, [])

  const onSubmit = async (data: ServerFormData) => {
    setIsLoading(true)
    
    try {
      const payload = {
        ...data,
        ports: data.ports.split(',').map(p => parseInt(p.trim())).filter(p => !isNaN(p)),
        sslPorts: data.sslPorts ? data.sslPorts.split(',').map(p => parseInt(p.trim())).filter(p => !isNaN(p)) : [],
        flags: selectedTags,
        ipv4: data.ipv4 || undefined,
        ipv6: data.ipv6 || undefined,
        contactUrl: data.contactUrl || undefined,
        city: data.city || undefined,
        region: data.region || undefined,
        sponsorId: data.sponsorId && data.sponsorId !== 'none' ? data.sponsorId : undefined,
        isActive: data.isActive,
      }

      const response = await fetch('/api/servers', {
        method: server ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(server ? { ...payload, id: server.id } : payload),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to save server')
      }

      toast({
        title: server ? 'Server updated' : 'Server created',
        description: server 
          ? `${data.displayName} has been updated successfully.`
          : `${data.displayName} has been created successfully.`,
      })
      
      onSuccess()
      router.refresh()
    } catch (error) {
      console.error('Error saving server:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save server',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hostname">Hostname *</Label>
              <Input
                id="hostname"
                {...register('hostname')}
                placeholder="irc.undernet.org"
              />
              {errors.hostname && (
                <p className="text-sm text-red-600">{errors.hostname.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
                {...register('displayName')}
                placeholder="Undernet Main Server"
              />
              {errors.displayName && (
                <p className="text-sm text-red-600">{errors.displayName.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="countryCode">Country *</Label>
              <Select
                value={watch('countryCode')}
                onValueChange={(value) => setValue('countryCode', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.countryCode && (
                <p className="text-sm text-red-600">{errors.countryCode.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                {...register('city')}
                placeholder="New York"
              />
            </div>
            
            <div>
              <Label htmlFor="region">Region *</Label>
              <Select
                value={watch('region')}
                onValueChange={(value) => setValue('region', value as 'Americas' | 'Asia-Pacific' | 'Europe' | 'IMEA' | 'Global')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Americas">Americas</SelectItem>
                  <SelectItem value="Asia-Pacific">Asia-Pacific</SelectItem>
                  <SelectItem value="Europe">Europe</SelectItem>
                  <SelectItem value="IMEA">IMEA</SelectItem>
                  <SelectItem value="Global">Global</SelectItem>
                </SelectContent>
              </Select>
              {errors.region && (
                <p className="text-sm text-red-600">{errors.region.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ipv4">IPv4 Address</Label>
              <Input
                id="ipv4"
                {...register('ipv4')}
                placeholder="192.168.1.1"
              />
              {errors.ipv4 && (
                <p className="text-sm text-red-600">{errors.ipv4.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="ipv6">IPv6 Address</Label>
              <Input
                id="ipv6"
                {...register('ipv6')}
                placeholder="2001:db8::1"
              />
              {errors.ipv6 && (
                <p className="text-sm text-red-600">{errors.ipv6.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ports">Ports (comma-separated) *</Label>
              <Input
                id="ports"
                {...register('ports')}
                placeholder="6667,6697"
              />
              {errors.ports && (
                <p className="text-sm text-red-600">{errors.ports.message}</p>
              )}
            </div>
            
            {ircSslEnabled && (
              <div>
                <Label htmlFor="sslPorts">SSL Ports (comma-separated)</Label>
                <Input
                  id="sslPorts"
                  {...register('sslPorts')}
                  placeholder="6697"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value as 'online' | 'offline' | 'unknown')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="flags">Server Tags</Label>
              <MultiSelect
                options={SERVER_TAG_OPTIONS}
                selected={selectedTags}
                onSelectedChange={setSelectedTags}
                placeholder="Select server tags..."
                className="mt-2"
              />
              <div className="text-sm text-muted-foreground mt-1">
                <p>
                  Select the appropriate tags for this server. Tags help categorize servers and affect their display priority.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sponsorId">Sponsor</Label>
              <Select
                value={watch('sponsorId') || 'none'}
                onValueChange={(value) => setValue('sponsorId', value === 'none' ? '' : value)}
                disabled={sponsorsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={sponsorsLoading ? 'Loading...' : 'Select sponsor (optional)'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No sponsor</SelectItem>
                  {sponsors.map((sponsor) => (
                    <SelectItem key={sponsor.id} value={sponsor.id}>
                      <div className="flex items-center space-x-2">
                        <span>{sponsor.name}</span>
                        {sponsor.logoUrl && (
                          <img
                            src={sponsor.logoUrl}
                            alt={sponsor.name}
                            className="w-4 h-2 object-contain"
                          />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {watch('sponsorId') && watch('sponsorId') !== 'none' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open('/dashboard/sponsors', '_blank')}
                  className="mt-2"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Manage Sponsors
                </Button>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={watch('isActive')}
                onCheckedChange={(checked) => setValue('isActive', checked)}
              />
              <Label htmlFor="isActive">Active</Label>
              <p className="text-sm text-gray-500">
                Only active servers appear on the public server list
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="contactUrl">Contact URL</Label>
            <Input
              id="contactUrl"
              {...register('contactUrl')}
              placeholder="https://undernet.org/contact"
            />
            {errors.contactUrl && (
              <p className="text-sm text-red-600">{errors.contactUrl.message}</p>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {server ? 'Update Server' : 'Create Server'}
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          </div>
    </form>
  )
}